import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '../../theme/scalingTokens';

export const useFormStepStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
    [mq.mobile]: {
      rowGap: tokens.spacingVerticalM,
    },
  },
  /** Figma 2026 form-step root: single white card containing column of section rows. */
  rootFigma: {
    rowGap: '0',
  },
  /** Figma 2026 section row: 2-column grid — icon+heading area and fields area. */
  sectionRowFigma: {
    display: 'grid',
    // Narrower header column gives the fields area enough room to keep two
    // inputs side-by-side at desktop widths (spec ask: "≥2 fields per row").
    gridTemplateColumns: 'minmax(17rem, 25rem) 1fr',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalM,
    alignItems: 'start',
    padding: tokens.spacingHorizontalXL,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    ':last-of-type': {
      borderBottomWidth: '0',
    },
    // Progressively tighten the header→fields column so the two areas sit
    // closer together as the viewport shrinks, before collapsing to a single
    // column at the tablet breakpoint.
    [mq.wide]: {
      gridTemplateColumns: 'minmax(14rem, 20rem) 1fr',
      columnGap: tokens.spacingHorizontalXL,
    },
    [mq.smallDesktop]: {
      gridTemplateColumns: 'minmax(10rem, 18rem) 1fr',
      columnGap: tokens.spacingHorizontalL,
    },
    [mq.tablet]: {
      gridTemplateColumns: '1fr',
      columnGap: 0,
      rowGap: tokens.spacingVerticalM,
    },
  },
  /** Left column: icon (top-left) + heading column (title + helper). */
  sectionHeaderArea: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: tokens.spacingHorizontalM,
    alignItems: 'start',
  },
  /**
   * Section glyph (Where/When/Attendees…). The source SVGs ship with a baked-in
   * navy `fill`, so we render them as a CSS mask and tint with the themed
   * `sectionIconFg` token. This keeps the glyph the exact same blue as the
   * booking stepper number circle in both light and dark modes (spec 101).
   */
  sectionHeaderIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.125rem',
    height: '1.25rem',
    marginTop: '0.375rem',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
    '& svg': {
      width: '100%',
      height: '100%',
      display: 'block',
    },
  },
  sectionHeaderText: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '0.25rem',
    minWidth: 0,
  },
  sectionTitleFigma: {
    fontSize: fontScale.title,
    lineHeight: '1.75rem',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  sectionDescriptionFigma: {
    fontSize: fontScale.body,
    lineHeight: '1.25rem',
    color: tokens.colorNeutralForeground2,
  },
  sectionFieldsArea: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    minWidth: 0,
    // Slight inward shift so the fields don't sit flush against the header
    // column; collapses on small-desktop and below where the section becomes
    // 1-column and the fields use the full row width.
    paddingLeft: tokens.spacingHorizontalS,
    [mq.tablet]: {
      paddingLeft: 0,
    },
  },
  sectionTip: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    paddingTop: tokens.spacingVerticalXS,
    '& .fui-Text': {
      fontSize: fontScale.caption,
    },
  },
  sectionTipText: {
    fontSize: fontScale.caption,
  },
  sectionTipIcon: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  stepDescription: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalS,
    lineHeight: tokens.lineHeightBase300,
  },
  sectionHeader: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalM,
    lineHeight: tokens.lineHeightBase300,
    [mq.mobile]: {
      marginBottom: tokens.spacingVerticalSNudge,
    },
  },
  sectionWrapper: {
    paddingTop: tokens.spacingVerticalL,
    ':not(:first-child)': {
      ...shorthands.borderTop('0.0625rem', 'solid', tokens.colorNeutralStroke2),
      marginTop: tokens.spacingVerticalL,
    },
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingVerticalL,
    '& > *': {
      minWidth: 0,
    },
    // Keep two fields per row only on full desktop (>1024px). Collapse to a
    // single column at small-desktop and below so each field gets the full
    // row width and dropdown labels/inputs no longer overlap.
    [mq.smallDesktop]: {
      gridTemplateColumns: '1fr',
      gap: tokens.spacingVerticalM,
    },
  },
  colSpan2: {
    gridColumn: 'span 2',
    [mq.smallDesktop]: {
      gridColumn: 'span 1',
    },
  },
  sectionLightBlue: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
    ...shorthands.border('0.0625rem', 'solid', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    marginTop: tokens.spacingVerticalM,
  },
});
